import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction } from '@yuan-math/math';
import {
  choiceExercise,
  expr,
  expressionExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  signedConstant,
  signedTerm,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * The Grade 12 calculus pathway: continuity, derivatives and their uses,
 * antiderivatives, Riemann sums and the fundamental theorem.
 *
 * Several of these are built so that the *wrong* answer is reachable, which for
 * calculus takes more care than it does lower down. Most calculus errors
 * produce something that is not a number at all — a half-differentiated
 * expression, an equation with no solution — and an item whose trap a student
 * cannot type into the box catches nobody.
 *
 * `continuity-constant` uses two straight branches rather than a line meeting a
 * parabola. The misconception worth catching is reporting the right-hand limit
 * as the answer, and with a quadratic branch that wrong answer is a² — which
 * collides with the correct one for exactly one choice of parameters and is
 * indistinguishable from an arithmetic slip for the rest.
 *
 * `related-rates-ladder` traps zero, and zero is the whole point of it. A
 * student who substitutes x = 6 into x² + y² = 100 before differentiating gets
 * 36 + y² = 100, a constant equation, and differentiating *that* gives
 * 2y·dy/dt = 0. The answer is not a slip in the arithmetic; it is a coherent
 * consequence of doing the two steps in the wrong order, and it is the only
 * wrong answer this item accepts.
 *
 * `riemann-left-sum` uses strips of width 2 rather than width 1. With unit
 * strips, multiplying by Δx changes nothing, so the student who never
 * multiplies gets the right answer and the misconception the item exists to
 * find is invisible.
 *
 * `definite-integral-polynomial` rejects any parameter set whose integral is
 * zero. F(b) − F(a) and F(a) − F(b) are the same number when that number is
 * zero, and the trap would then be sitting on the answer — which
 * `generateExercise` would strip, leaving an item that diagnoses nothing.
 *
 * `chain-rule-power` keeps the inner coefficient at 2 or more. At a = 1 the
 * chain-rule factor is 1, the wrong answer and the right answer coincide, and
 * the item quietly stops testing the rule it is named after.
 *
 * `antiderivative-polynomial` asks for the antiderivative through the origin.
 * An expression answer cannot carry a + C — there is nothing for the grader to
 * match it against — so the question that would reward writing one is asked as
 * multiple choice instead, in `antiderivative-family`, and this item pins the
 * constant with a condition so that a single expression is the honest answer.
 */

/** Right triangles with whole-number sides, for exact related-rates values. */
const TRIPLES: readonly [number, number, number][] = [
  [3, 4, 5],
  [5, 12, 13],
  [8, 15, 17],
  [7, 24, 25],
];

/** A number for prose rather than for LaTeX — a real minus sign, not a hyphen. */
const minus = (value: number | string): string => String(value).replace(/-/g, '−');

/** `3x^{2}` as an expression the grader can parse: `3*x^2`. */
const term = (coefficient: number, power: number): string => {
  if (coefficient === 0) return '0';
  if (power === 0) return `${coefficient}`;
  if (power === 1) return `${coefficient}*x`;
  return `${coefficient}*x^${power}`;
};

/** Joins parsed terms into a sum, dropping the zero ones. */
const sum = (...parts: string[]): string => {
  const kept = parts.filter((part) => part !== '0');
  return kept.length === 0 ? '0' : kept.join(' + ');
};

export const CALCULUS_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------------ continuity */
  template({
    id: 'continuity-constant',
    conceptId: 'continuity',
    description: 'Find the constant that closes a gap between two branches.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 6);
      const p = rng.int(1, 5);
      let q = rng.int(1, 6);
      if (q === p) q = p + 1; // Equal slopes make the branches parallel and the gap constant.
      const r = rng.int(-6, 6);
      const answer = (q - p) * a + r;
      const rightLimit = q * a + r;

      return numericExercise({
        prompt: `For what value of k is this function continuous at x = ${a}?`,
        latex: `f(x) = \\begin{cases} ${p}x + k & x < ${a} \\\\ ${expr(signedTerm(q, 'x'), signedConstant(r)).replace(/^\+\s*/, '')} & x \\geq ${a} \\end{cases}`,
        value: answer,
        summary: `The right-hand limit is ${q}(${a}) ${minus(signedConstant(r))} = ${minus(rightLimit)}, and the left-hand limit is ${p}(${a}) + k. Continuity needs them equal, so k = ${minus(answer)}.`,
        hint: 'Set the two one-sided limits equal to each other, then solve for k.',
        steps: [
          { label: 'Left-hand limit, from the top branch.', latex: `\\lim_{x \\to ${a}^{-}} f(x) = ${p}(${a}) + k` },
          { label: 'Right-hand limit, from the bottom branch.', latex: `\\lim_{x \\to ${a}^{+}} f(x) = ${rightLimit}` },
          { label: 'Continuity needs them equal.', latex: `${p * a} + k = ${rightLimit}` },
          { label: 'Solve.', latex: `k = ${answer}` },
        ],
        // Reporting the right-hand limit itself, rather than the k that reaches it.
        traps: [trap(rightLimit, 'one-sided-limits-not-compared')],
      });
    },
  }),

  template({
    id: 'one-sided-limit-value',
    conceptId: 'continuity',
    description: 'Read a one-sided limit off a piecewise definition.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 6);
      const p = rng.int(2, 6);
      const q = rng.int(-5, 5);
      let r = rng.int(-8, 8);
      const answer = p * a + q;
      if (r === answer) r = answer + 3; // A matching branch would make it continuous, and the item pointless.

      return numericExercise({
        prompt: `Find the left-hand limit as x approaches ${a}.`,
        latex: `f(x) = \\begin{cases} ${expr(signedTerm(p, 'x'), signedConstant(q)).replace(/^\+\s*/, '')} & x < ${a} \\\\ ${r} & x \\geq ${a} \\end{cases}`,
        value: answer,
        summary: `Approaching from the left means x is just below ${a}, where the first branch applies. So the limit is ${p}(${a}) ${minus(signedConstant(q))} = ${minus(answer)}.`,
        hint: 'The small minus sign is a direction — approach from below, and use whichever branch covers x < ' + a + '.',
        // Taking the value from the branch that owns the point instead of the one being approached.
        traps: [trap(r, 'one-sided-limits-not-compared')],
      });
    },
  }),

  template({
    id: 'jump-limit-exists',
    conceptId: 'continuity',
    description: 'Decide whether a limit exists where the two branches disagree.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 5);
      const left = rng.int(1, 8);
      let right = rng.int(1, 8);
      if (right === left) right = left + rng.int(1, 4); // No jump, no question.
      const midpoint = (left + right) / 2;

      return choiceExercise({
        prompt: `A function equals ${left} for every x < ${a} and ${right} for every x ≥ ${a}. What is the limit as x approaches ${a}?`,
        ...options(
          [
            { label: 'The limit does not exist', correct: true },
            { latex: `${left}`, misconceptionId: 'one-sided-limits-not-compared' },
            { latex: `${right}`, misconceptionId: 'defined-taken-as-continuous' },
            { latex: `${midpoint}` },
          ],
          rng.shuffle,
        ),
        summary: `Both one-sided limits exist — ${left} from the left and ${right} from the right — and they disagree, so there is no two-sided limit. The function is defined at x = ${a}, which settles nothing: being defined is only the first of the three conditions for continuity.`,
        hint: 'A two-sided limit exists only when the two one-sided limits agree.',
      });
    },
  }),

  /* ----------------------------------------------------------- derivatives */
  template({
    id: 'polynomial-derivative',
    conceptId: 'derivatives',
    description: 'Differentiate a cubic term by term.',
    difficulties: [2, 3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 6);
      const b = rng.int(-6, 6);
      const c = rng.int(-8, 8);
      let d = rng.int(-9, 9);
      if (d === 0) d = 5; // A zero constant makes the "kept the constant" trap the right answer.

      const answer = sum(term(3 * a, 2), term(2 * b, 1), term(c, 0));
      const keptConstant = sum(term(3 * a, 2), term(2 * b, 1), term(c, 0), term(d, 0));
      const exponentsKept = sum(term(3 * a, 3), term(2 * b, 2), term(c, 1));
      const noCoefficients = sum(term(a, 2), term(b, 1), term(c, 0));

      return expressionExercise({
        prompt: 'Differentiate with respect to x.',
        latex: `f(x) = ${expr(`${a === 1 ? '' : a}x^{3}`, signedTerm(b, 'x^{2}'), signedTerm(c, 'x'), signedConstant(d))}`,
        canonical: answer,
        summary: `Each power comes down as a multiplier and drops by one, and the constant ${minus(d)} contributes nothing: f′(x) = ${minus(answer.replace(/\*/g, ''))}.`,
        hint: 'Bring each power down, reduce it by one, and remember what a constant differentiates to.',
        steps: [
          { label: 'The cubic term.', latex: `\\frac{d}{dx}\\big(${a}x^{3}\\big) = ${3 * a}x^{2}` },
          { label: 'The square term.', latex: `\\frac{d}{dx}\\big(${b}x^{2}\\big) = ${2 * b}x` },
          { label: 'The linear term keeps its coefficient.', latex: `\\frac{d}{dx}\\big(${c}x\\big) = ${c}` },
          { label: 'The constant vanishes.', latex: `\\frac{d}{dx}\\big(${d}\\big) = 0` },
        ],
        traps: [
          trap(keptConstant, 'constant-derivative-kept'),
          trap(exponentsKept, 'power-rule-exponent-not-reduced'),
          ...(a === 3 ? [] : [trap(noCoefficients, 'power-rule-coefficient-omitted')]),
        ],
      });
    },
  }),

  template({
    id: 'derivative-at-a-point',
    conceptId: 'derivatives',
    description: 'Evaluate a derivative at a named value.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 5);
      const b = rng.int(-6, 6);
      const c = rng.int(-8, 8);
      const d = rng.int(-9, 9);
      const at = rng.int(1, 4);
      const answer = 3 * a * at * at + 2 * b * at + c;
      const withoutCoefficients = a * at * at + b * at + c;
      const valueOfF = a * at ** 3 + b * at * at + c * at + d;

      return numericExercise({
        prompt: `Find f′(${at}).`,
        latex: `f(x) = ${expr(`${a === 1 ? '' : a}x^{3}`, signedTerm(b, 'x^{2}'), signedTerm(c, 'x'), signedConstant(d))}`,
        value: answer,
        summary: `f′(x) = ${minus(sum(term(3 * a, 2), term(2 * b, 1), term(c, 0)).replace(/\*/g, ''))}, and substituting x = ${at} gives ${minus(answer)}.`,
        hint: 'Differentiate first, then substitute — f′(x) and f′(' + at + ') are different objects.',
        steps: [
          { label: 'Differentiate.', latex: `f'(x) = ${3 * a}x^{2} ${signedTerm(2 * b, 'x')} ${signedConstant(c)}` },
          { label: `Substitute x = ${at}.`, latex: `${3 * a}(${at * at}) ${signedConstant(2 * b * at)} ${signedConstant(c)}` },
          { label: 'Arithmetic.', latex: `${answer}` },
        ],
        traps: [
          ...(withoutCoefficients === answer ? [] : [trap(withoutCoefficients, 'power-rule-coefficient-omitted')]),
          ...(valueOfF === answer ? [] : [trap(valueOfF, 'constant-derivative-kept')]),
        ],
      });
    },
  }),

  template({
    id: 'derivative-of-a-constant',
    conceptId: 'derivatives',
    description: 'Differentiate a function whose only term is a constant.',
    difficulties: [1, 2],
    build: ({ rng }) => {
      const k = rng.int(2, 30);

      return choiceExercise({
        prompt: `What is the derivative of f(x) = ${k}?`,
        ...options(
          [
            { latex: '0', correct: true },
            { latex: `${k}`, misconceptionId: 'constant-derivative-kept' },
            { latex: `${k}x`, misconceptionId: 'antiderivative-differentiated-instead' },
            { latex: '1' },
          ],
          rng.shuffle,
        ),
        summary: `y = ${k} is a horizontal line, and a horizontal line has slope zero everywhere. It is also the power rule with n = 0: ${k} is ${k}x⁰, and the rule brings that 0 down as a multiplier.`,
        hint: 'Sketch it. What is the slope of a horizontal line?',
      });
    },
  }),

  /* ------------------------------------------------------ derivative rules */
  template({
    id: 'chain-rule-power',
    conceptId: 'derivative-rules',
    description: 'Differentiate a bracket raised to a power.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // a = 1 would make the chain-rule factor 1, and the trap would be the answer.
      const a = rng.int(2, 6);
      const b = rng.int(-8, 8);
      const n = rng.int(3, 6);
      const inner = `(${a}*x ${b < 0 ? '-' : '+'} ${Math.abs(b)})`;
      const answer = `${n * a}*${inner}^${n - 1}`;
      const withoutInner = `${n}*${inner}^${n - 1}`;

      return expressionExercise({
        prompt: 'Differentiate with respect to x.',
        latex: `y = \\big(${expr(signedTerm(a, 'x'), signedConstant(b)).replace(/^\+\s*/, '')}\\big)^{${n}}`,
        canonical: answer,
        summary: `The outside gives ${n}(${minus(`${a}x`)}${minus(signedConstant(b))})^${n - 1}, and the inside gives ${a}. Multiplying, dy/dx = ${n * a}(${minus(`${a}x`)}${minus(signedConstant(b))})^${n - 1}.`,
        hint: 'Differentiate the outside, leave the bracket alone, then multiply by the derivative of the bracket.',
        steps: [
          { label: 'Differentiate the outside; the bracket is untouched.', latex: `${n}\\big(${a}x ${signedConstant(b)}\\big)^{${n - 1}}` },
          { label: 'Differentiate the inside.', latex: `\\frac{d}{dx}\\big(${a}x ${signedConstant(b)}\\big) = ${a}` },
          { label: 'Multiply the two.', latex: `${n * a}\\big(${a}x ${signedConstant(b)}\\big)^{${n - 1}}` },
        ],
        // The answer with the right shape and the wrong constant: the inside derivative dropped.
        traps: [trap(withoutInner, 'chain-rule-inner-derivative-dropped')],
      });
    },
  }),

  template({
    id: 'product-rule-expand',
    conceptId: 'derivative-rules',
    description: 'Differentiate a product of two linear factors.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 6);
      const b = rng.int(-8, 8);
      const c = rng.int(1, 6);
      const d = rng.int(-8, 8);
      const answer = sum(term(2 * a * c, 1), term(a * d + b * c, 0));
      const productOfDerivatives = `${a * c}`;

      return expressionExercise({
        prompt: 'Differentiate with respect to x.',
        latex: `y = \\big(${expr(signedTerm(a, 'x'), signedConstant(b)).replace(/^\+\s*/, '')}\\big)\\big(${expr(signedTerm(c, 'x'), signedConstant(d)).replace(/^\+\s*/, '')}\\big)`,
        canonical: answer,
        summary: `By the product rule, ${a}(${minus(`${c}x`)}${minus(signedConstant(d))}) + ${c}(${minus(`${a}x`)}${minus(signedConstant(b))}) = ${minus(answer.replace(/\*/g, ''))}.`,
        hint: 'Derivative of the first times the second, plus the first times derivative of the second.',
        steps: [
          { label: 'u′v — derivative of the first, times the second.', latex: `${a}\\big(${c}x ${signedConstant(d)}\\big)` },
          { label: 'uv′ — the first, times derivative of the second.', latex: `${c}\\big(${a}x ${signedConstant(b)}\\big)` },
          { label: 'Add and collect.', latex: `${2 * a * c}x ${signedConstant(a * d + b * c)}` },
        ],
        // u′v′ — the guess the product rule exists to refute.
        traps: [trap(productOfDerivatives, 'product-rule-as-product-of-derivatives')],
      });
    },
  }),

  template({
    id: 'quotient-rule-numerator',
    conceptId: 'derivative-rules',
    description: 'Pick the correct numerator for a quotient derivative.',
    difficulties: [3, 4],
    build: ({ rng }) =>
      choiceExercise({
        prompt: 'For y = u/v, which expression is the numerator of dy/dx?',
        ...options(
          [
            { latex: "u'v - uv'", correct: true },
            { latex: "uv' - u'v", misconceptionId: 'quotient-rule-terms-reversed' },
            { latex: "u'v'", misconceptionId: 'product-rule-as-product-of-derivatives' },
            { latex: "u'v + uv'" },
          ],
          rng.shuffle,
        ),
        summary:
          'The top is differentiated first: (u′v − uv′)/v². Subtraction is not commutative, so writing the two terms the other way round negates the whole derivative — and a negated slope claims the curve rises where it falls.',
        hint: 'Which of the two functions gets differentiated in the first term?',
      }),
  }),

  template({
    id: 'which-rule',
    conceptId: 'derivative-rules',
    description: 'Choose the rule an expression needs.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const n = rng.int(4, 9);

      return choiceExercise({
        prompt: 'Which rule differentiates this most directly?',
        latex: `y = (${a}x + 1)^{${n}}`,
        ...options(
          [
            { label: 'The chain rule', correct: true },
            { label: 'The product rule', misconceptionId: 'product-rule-as-product-of-derivatives' },
            { label: 'The quotient rule' },
            { label: 'The power rule on its own', misconceptionId: 'chain-rule-inner-derivative-dropped' },
          ],
          rng.shuffle,
        ),
        summary: `The outermost operation is "raise to the power ${n}", and what is being raised is a function of x rather than x itself. That is a composition, so the chain rule applies — and the power rule alone would miss the factor of ${a}.`,
        hint: 'Ask what the outermost operation is, and whether the thing inside it is just x.',
      });
    },
  }),

  /* --------------------------------------------- implicit differentiation */
  template({
    id: 'implicit-derivative-form',
    conceptId: 'implicit-differentiation',
    description: 'Differentiate an implicit equation with respect to x.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const r = rng.int(3, 9);

      return choiceExercise({
        prompt: 'Differentiate both sides with respect to x. Which is correct?',
        latex: `x^{2} + y^{2} = ${r * r}`,
        ...options(
          [
            { latex: '2x + 2y\\frac{dy}{dx} = 0', correct: true },
            { latex: '2x + 2y = 0', misconceptionId: 'implicit-dy-dx-omitted' },
            { latex: `2x + 2y\\frac{dy}{dx} = ${2 * r}` },
            { latex: '2x\\frac{dy}{dx} + 2y = 0' },
          ],
          rng.shuffle,
        ),
        summary: `y is a function of x, so y² differentiates by the chain rule to 2y·dy/dx. The right-hand side is the constant ${r * r}, whose derivative is 0.`,
        hint: 'Every y you differentiate leaves a dy/dx behind. Count them.',
      });
    },
  }),

  template({
    id: 'implicit-slope-circle',
    conceptId: 'implicit-differentiation',
    description: 'Find the slope of a circle at a point on it.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const [p, q] = rng.pick(TRIPLES);
      const [x, y] = rng.bool() ? [p, q] : [q, p];
      const answer = fraction(-x, y);

      return fractionExercise({
        prompt: `Find dy/dx at the point (${x}, ${y}).`,
        latex: `x^{2} + y^{2} = ${x * x + y * y}`,
        value: answer,
        requireSimplified: true,
        summary: `Differentiating gives 2x + 2y·dy/dx = 0, so dy/dx = −x/y, which at (${x}, ${y}) is ${minus(`${-x}/${y}`)}.`,
        hint: 'Differentiate the equation as it stands, then make dy/dx the subject.',
        steps: [
          { label: 'Differentiate with respect to x.', latex: '2x + 2y\\frac{dy}{dx} = 0' },
          { label: 'Make dy/dx the subject.', latex: '\\frac{dy}{dx} = -\\frac{x}{y}' },
          { label: 'Substitute the point.', latex: `-\\frac{${x}}{${y}}` },
        ],
        // The reciprocal — dx/dy, which is a real quantity and a different question.
        traps: [fractionTrap(fraction(-y, x), 'implicit-dy-dx-omitted')],
      });
    },
  }),

  template({
    id: 'related-rates-ladder',
    conceptId: 'implicit-differentiation',
    description: 'Find one rate from another, at a named instant.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const [p, q, r] = rng.pick(TRIPLES);
      const scale = rng.int(1, 3);
      const [x, y, length] = [p * scale, q * scale, r * scale];
      const speed = rng.int(1, 4);
      const answer = (x * speed) / y;

      return numericExercise({
        prompt: `A ${length} m ladder leans against a wall. Its foot is pulled away from the wall at ${speed} m/s. How fast is the top sliding down when the foot is ${x} m from the wall? Give the speed in m/s.`,
        unit: 'm/s',
        value: answer,
        tolerance: within(0.005, answer),
        summary: `x² + y² = ${length * length} holds at every instant. Differentiating with respect to t gives 2x·dx/dt + 2y·dy/dt = 0, and at this moment y = ${y}, so dy/dt = −(${x})(${speed})/${y}, a fall of ${minus(round2(answer))} m/s.`,
        hint: 'Write Pythagoras for the ladder, differentiate with respect to time, and only then put the numbers in.',
        steps: [
          { label: 'Relate the quantities — true at every instant.', latex: `x^{2} + y^{2} = ${length * length}` },
          { label: 'Differentiate with respect to t.', latex: '2x\\frac{dx}{dt} + 2y\\frac{dy}{dt} = 0' },
          { label: 'Find y at this instant.', latex: `y = \\sqrt{${length * length} - ${x * x}} = ${y}` },
          { label: 'Substitute and solve.', latex: `\\frac{dy}{dt} = -\\frac{${x}(${speed})}{${y}} = ${-round2(answer)}` },
        ],
        /*
         * Zero is what substituting x before differentiating actually produces:
         * the relationship becomes a constant equation, and differentiating a
         * constant gives 2y·dy/dt = 0.
         */
        traps: [trap(0, 'related-rates-substituted-early')],
      });
    },
  }),

  /* ----------------------------------------------- shape from the derivative */
  template({
    id: 'local-maximum-value',
    conceptId: 'derivative-applications',
    description: 'Find the value of a local maximum, not merely where it is.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Built from the critical points outwards. f'(x) = 3(x − m)(x − n) needs
       * m + n even for the coefficients of f to stay whole.
       */
      const m = rng.int(-4, 2);
      const gap = 2 * rng.int(1, 3);
      const n = m + gap;
      const b = (-3 * (m + n)) / 2;
      const c = 3 * m * n;
      const d = rng.int(-6, 6);
      const answer = m ** 3 + b * m * m + c * m + d;

      return numericExercise({
        prompt: 'Find the local maximum value of f.',
        latex: `f(x) = ${expr('x^{3}', signedTerm(b, 'x^{2}'), signedTerm(c, 'x'), signedConstant(d))}`,
        value: answer,
        summary: `f′(x) = 3(x − ${minus(m)})(x − ${minus(n)}), so the critical points are x = ${minus(m)} and x = ${minus(n)}. The leading coefficient is positive, so the smaller one is the maximum: f(${minus(m)}) = ${minus(answer)}.`,
        hint: 'Find where f′ = 0, decide which one is the maximum, and then substitute back into f.',
        steps: [
          { label: 'Differentiate.', latex: `f'(x) = 3x^{2} ${signedTerm(2 * b, 'x')} ${signedConstant(c)}` },
          { label: 'Factorise and solve f′ = 0.', latex: `3(x - ${m})(x - ${n}) = 0` },
          { label: 'The cubic rises, falls, rises — so the smaller root is the maximum.', latex: `x = ${m}` },
          { label: 'Substitute back into f, not f′.', latex: `f(${m}) = ${answer}` },
        ],
        traps: [
          // Reporting where the maximum happens instead of what it is.
          ...(m === answer ? [] : [trap(m, 'extremum-reported-as-x-value')]),
          // The other critical point: the minimum, taken without testing which is which.
          ...(() => {
            const other = n ** 3 + b * n * n + c * n + d;
            return other === answer ? [] : [trap(other, 'stationary-point-assumed-extremum')];
          })(),
        ],
      });
    },
  }),

  template({
    id: 'stationary-not-turning',
    conceptId: 'derivative-applications',
    description: 'Decide whether a stationary point is an extremum.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 5);

      return choiceExercise({
        prompt: 'This function has f′(0) = 0. What happens at x = 0?',
        latex: `f(x) = ${a === 1 ? '' : a}x^{3}`,
        ...options(
          [
            { label: 'Neither a maximum nor a minimum — the curve flattens and carries on', correct: true },
            { label: 'A local maximum', misconceptionId: 'stationary-point-assumed-extremum' },
            { label: 'A local minimum', misconceptionId: 'stationary-point-assumed-extremum' },
            { label: 'The function is undefined there' },
          ],
          rng.shuffle,
        ),
        summary: `f′(x) = ${3 * a}x², which is positive either side of 0 and zero only at 0 itself. The slope never changes sign, so the curve is climbing before and after — it merely pauses. f′(c) = 0 makes c a candidate, never an answer.`,
        hint: 'Check the sign of f′ just left and just right of zero.',
      });
    },
  }),

  template({
    id: 'increasing-or-concave',
    conceptId: 'derivative-applications',
    description: 'Separate the direction of a curve from the way it bends.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const slope = rng.int(2, 9);
      const bend = rng.int(2, 9);

      return choiceExercise({
        prompt: `At a point, f′ = ${minus(-slope)} and f″ = ${bend}. What is the curve doing there?`,
        ...options(
          [
            { label: 'Falling, and concave up — slowing its fall', correct: true },
            { label: 'Rising, and concave up', misconceptionId: 'concavity-confused-with-direction' },
            { label: 'Falling, and concave down' },
            { label: 'At a minimum' },
          ],
          rng.shuffle,
        ),
        summary:
          'f′ is negative, so the curve is falling; f″ is positive, so it is concave up. Those are answers to different questions — the first is direction, the second is bend — and a falling curve that is concave up is one whose fall is easing off.',
        hint: 'f′ says which way. f″ says how it bends. Answer them one at a time.',
      });
    },
  }),

  /* --------------------------------------------------------- optimisation */
  template({
    id: 'fence-max-area',
    conceptId: 'optimization',
    description: 'Maximise an enclosed area against a fixed length of fence.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const fence = 4 * rng.int(5, 30);
      const depth = fence / 4;
      const answer = (fence * fence) / 8;

      return numericExercise({
        prompt: `You have ${fence} m of fencing and a straight river. Using the river as one side, what is the largest rectangular area you can enclose? Give the area in square metres.`,
        unit: 'm²',
        value: answer,
        summary: `With depth x, the fence gives 2x + y = ${fence}, so A(x) = x(${fence} − 2x). A′(x) = ${fence} − 4x is zero at x = ${depth}, and the pen is ${depth} m by ${fence - 2 * depth} m — an area of ${answer} m².`,
        hint: 'Write the area, use the fencing to remove one variable, then differentiate.',
        steps: [
          { label: 'The quantity to maximise.', latex: 'A = xy' },
          { label: 'The constraint — three sides only.', latex: `2x + y = ${fence}` },
          { label: 'Eliminate y.', latex: `A(x) = x(${fence} - 2x)` },
          { label: 'Differentiate and set to zero.', latex: `A'(x) = ${fence} - 4x = 0` },
          { label: 'Solve, then answer the question.', latex: `x = ${depth}, \\quad A = ${answer}` },
        ],
        traps: [
          // Stopping at the critical value — the calculus finished, the question not.
          trap(depth, 'optimisation-answered-with-variable'),
          // The all-four-sides answer: the constraint written without noticing the river.
          trap((fence * fence) / 16, 'optimisation-constraint-differentiated'),
        ],
      });
    },
  }),

  template({
    id: 'rectangle-max-area',
    conceptId: 'optimization',
    description: 'Maximise the area of a rectangle of fixed perimeter.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const perimeter = 4 * rng.int(4, 25);
      const side = perimeter / 4;
      const answer = side * side;

      return numericExercise({
        prompt: `A rectangle has perimeter ${perimeter} cm. What is its largest possible area, in square centimetres?`,
        unit: 'cm²',
        value: answer,
        summary: `With 2x + 2y = ${perimeter}, y = ${perimeter / 2} − x and A(x) = x(${perimeter / 2} − x). A′(x) = ${perimeter / 2} − 2x is zero at x = ${side}, which makes it a square of side ${side} and area ${answer} cm².`,
        hint: 'Use the perimeter to write y in terms of x, then maximise the area.',
        steps: [
          { label: 'The constraint.', latex: `2x + 2y = ${perimeter} \\;\\Rightarrow\\; y = ${perimeter / 2} - x` },
          { label: 'The area in one variable.', latex: `A(x) = x(${perimeter / 2} - x)` },
          { label: 'Differentiate and solve.', latex: `A'(x) = ${perimeter / 2} - 2x = 0 \\;\\Rightarrow\\; x = ${side}` },
          { label: 'Answer the question, not the algebra.', latex: `A = ${side} \\times ${side} = ${answer}` },
        ],
        traps: [trap(side, 'optimisation-answered-with-variable')],
      });
    },
  }),

  template({
    id: 'optimisation-which-equation',
    conceptId: 'optimization',
    description: 'Decide which of the two equations is differentiated.',
    difficulties: [2, 3],
    build: ({ rng }) =>
      choiceExercise({
        prompt: 'A box of fixed surface area is to have the largest possible volume. Which equation do you differentiate?',
        ...options(
          [
            { label: 'The volume — the quantity being maximised', correct: true },
            { label: 'The surface area — the fixed constraint', misconceptionId: 'optimisation-constraint-differentiated' },
            { label: 'Both, and set them equal' },
            { label: 'Neither; compare a few cases instead' },
          ],
          rng.shuffle,
        ),
        summary:
          'The constraint is used to eliminate a variable, so that the volume becomes a function of one thing; that function is then differentiated. Differentiating the constraint answers a question nobody asked — it is fixed, so its rate of change is zero by assumption.',
        hint: 'Which of the two is allowed to vary, and which is fixed by the problem?',
      }),
  }),

  /* --------------------------------------------- velocity and acceleration */
  template({
    id: 'velocity-at-time',
    conceptId: 'motion-derivatives',
    description: 'Find a velocity by differentiating a position function.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const b = rng.int(-8, 8);
      const c = rng.int(-9, 9);
      const at = rng.int(1, 5);
      const answer = 3 * a * at * at + 2 * b * at + c;
      const position = a * at ** 3 + b * at * at + c * at;

      return numericExercise({
        prompt: `A particle has position s(t) metres at time t seconds. Find its velocity at t = ${at}.`,
        latex: `s(t) = ${expr(`${a === 1 ? '' : a}t^{3}`, signedTerm(b, 't^{2}'), signedTerm(c, 't'))}`,
        unit: 'm/s',
        value: answer,
        summary: `v(t) = s′(t) = ${minus(`${3 * a}t²`)} ${minus(signedTerm(2 * b, 't'))} ${minus(signedConstant(c))}, and at t = ${at} that is ${minus(answer)} m/s.`,
        hint: 'Velocity is the derivative of position. Differentiate first, then substitute.',
        steps: [
          { label: 'Differentiate.', latex: `v(t) = ${3 * a}t^{2} ${signedTerm(2 * b, 't')} ${signedConstant(c)}` },
          { label: `Substitute t = ${at}.`, latex: `${answer}` },
        ],
        // Reporting where it is rather than how fast it is going.
        traps: [...(position === answer ? [] : [trap(position, 'speed-given-as-signed-velocity')])],
      });
    },
  }),

  template({
    id: 'speed-not-velocity',
    conceptId: 'motion-derivatives',
    description: 'Give a speed where the velocity is negative.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 3);
      const at = rng.int(1, 3);
      const b = -rng.int(4, 9);
      const c = rng.int(-4, 4);
      const velocity = 3 * a * at * at + 2 * b * at + c;
      // The item only says anything if the velocity really is negative.
      const signed = velocity < 0 ? velocity : -Math.abs(velocity) - 1;
      const answer = Math.abs(signed);

      return numericExercise({
        prompt: `At one instant a particle has velocity ${minus(signed)} m/s. What is its speed?`,
        unit: 'm/s',
        value: answer,
        summary: `Speed is the size of the velocity and is never negative, so the speed is ${answer} m/s. The sign said the particle was moving in the negative direction; the question asked how fast, not which way.`,
        hint: 'Speed is |v|. Which of the two did the question name?',
        traps: [trap(signed, 'speed-given-as-signed-velocity')],
      });
    },
  }),

  template({
    id: 'speeding-up-or-slowing',
    conceptId: 'motion-derivatives',
    description: 'Read speeding up and slowing down off two signs.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const v = rng.int(2, 9);
      const a = rng.int(2, 9);

      return choiceExercise({
        prompt: `At one instant v = ${minus(-v)} m/s and a = ${minus(-a)} m/s². What is the particle doing?`,
        ...options(
          [
            { label: 'Moving in the negative direction, speeding up', correct: true },
            { label: 'Moving in the negative direction, slowing down', misconceptionId: 'concavity-confused-with-direction' },
            { label: 'Moving in the positive direction, slowing down' },
            { label: 'At rest', misconceptionId: 'acceleration-assumed-zero-at-rest' },
          ],
          rng.shuffle,
        ),
        summary:
          'Both are negative, and matching signs mean the push is in the direction of travel — so the particle is speeding up while moving backwards. Acceleration is not "getting faster"; it is a push, and only a push against the motion is a brake.',
        hint: 'Same signs speed up; opposite signs slow down.',
      });
    },
  }),

  /* ------------------------------------------------------- antiderivatives */
  template({
    id: 'antiderivative-polynomial',
    conceptId: 'antiderivatives',
    description: 'Reverse the power rule on a quadratic.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = 3 * rng.int(1, 4);
      const b = 2 * rng.int(-4, 4);
      const c = rng.int(-8, 8);
      const answer = sum(term(a / 3, 3), term(b / 2, 2), term(c, 1));
      const differentiated = sum(term(2 * a, 1), term(b, 0));

      return expressionExercise({
        prompt: 'Find the antiderivative F with F(0) = 0.',
        latex: `f(x) = ${expr(`${a}x^{2}`, signedTerm(b, 'x'), signedConstant(c))}`,
        canonical: answer,
        summary: `Raise each power by one and divide by the new power: ${minus(answer.replace(/\*/g, ''))}. F(0) = 0 pins the constant at zero, so nothing more is added.`,
        hint: 'Run the power rule backwards — the power goes up, and you divide by the new one.',
        steps: [
          { label: 'The square term.', latex: `\\int ${a}x^{2}\\,dx = \\frac{${a}x^{3}}{3} = ${a / 3}x^{3}` },
          { label: 'The linear term.', latex: `\\int ${b}x\\,dx = \\frac{${b}x^{2}}{2} = ${b / 2}x^{2}` },
          { label: 'The constant term.', latex: `\\int ${c}\\,dx = ${c}x` },
          { label: 'F(0) = 0, so C = 0.', latex: answer.replace(/\*/g, '') },
        ],
        // Applying the power rule forwards — differentiating the very thing being undone.
        traps: [trap(differentiated, 'antiderivative-differentiated-instead')],
      });
    },
  }),

  template({
    id: 'antiderivative-family',
    conceptId: 'antiderivatives',
    description: 'Recognise that an indefinite integral is a family.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const k = rng.int(2, 9);

      return choiceExercise({
        prompt: 'Evaluate the indefinite integral.',
        latex: `\\int ${2 * k}x\\,dx`,
        ...options(
          [
            { latex: `${k}x^{2} + C`, correct: true },
            { latex: `${k}x^{2}`, misconceptionId: 'antiderivative-constant-omitted' },
            { latex: `${2 * k}`, misconceptionId: 'antiderivative-differentiated-instead' },
            { latex: `${2 * k}x^{2} + C` },
          ],
          rng.shuffle,
        ),
        summary: `The power rises to 2 and divides by 2, giving ${k}x². The + C is part of the answer: every function of the form ${k}x² + C differentiates to ${2 * k}x, so no single one of them is *the* antiderivative.`,
        hint: 'Differentiate each option and see which ones give back the integrand. More than one will.',
      });
    },
  }),

  template({
    id: 'antiderivative-initial-condition',
    conceptId: 'antiderivatives',
    description: 'Pin the constant of integration with a known point.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = 3 * rng.int(1, 3);
      const b = 2 * rng.int(1, 4);
      const at = rng.int(1, 3);
      const known = rng.int(2, 20);
      const withoutC = (a / 3) * at ** 3 + (b / 2) * at * at;
      const answer = known - withoutC;

      return numericExercise({
        prompt: `A function has the derivative below and passes through (${at}, ${known}). Find C.`,
        latex: `f'(x) = ${expr(`${a}x^{2}`, signedTerm(b, 'x'))}`,
        value: answer,
        summary: `Antidifferentiating gives f(x) = ${a / 3}x³ + ${b / 2}x² + C. At x = ${at} that is ${withoutC} + C, and it must equal ${known}, so C = ${minus(answer)}.`,
        hint: 'Antidifferentiate first, keeping the + C, then substitute the point and solve.',
        steps: [
          { label: 'Antidifferentiate, keeping the constant.', latex: `f(x) = ${a / 3}x^{3} + ${b / 2}x^{2} + C` },
          { label: `Substitute x = ${at}.`, latex: `${withoutC} + C = ${known}` },
          { label: 'Solve.', latex: `C = ${answer}` },
        ],
        // Answering with f(at) — the number substituted in, not the constant solved for.
        traps: [...(withoutC === answer ? [] : [trap(withoutC, 'antiderivative-constant-omitted')])],
      });
    },
  }),

  /* ------------------------------------------------------ area under a curve */
  template({
    id: 'riemann-left-sum',
    conceptId: 'integrals',
    description: 'Approximate an area with left-endpoint rectangles.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * Width 2, never 1. With unit strips the multiplication by Δx changes
       * nothing, and the student who never multiplies scores full marks.
       */
      const width = 2;
      const n = rng.int(3, 5);
      const b = width * n;
      let heights = 0;
      for (let i = 0; i < n; i += 1) heights += (width * i) ** 2;
      let rightHeights = 0;
      for (let i = 1; i <= n; i += 1) rightHeights += (width * i) ** 2;
      const answer = heights * width;

      return numericExercise({
        prompt: `Estimate the area under y = x² from x = 0 to x = ${b} using ${n} left-endpoint rectangles of equal width.`,
        value: answer,
        summary: `Each strip is ${width} wide, and the left edges are at ${Array.from({ length: n }, (_, i) => width * i).join(', ')}. The heights add to ${heights}, and multiplying by the common width gives ${answer}.`,
        hint: 'Add the heights first, then multiply the total by the strip width once.',
        steps: [
          { label: 'Strip width.', latex: `\\Delta x = \\frac{${b} - 0}{${n}} = ${width}` },
          { label: 'Heights at the left edges.', latex: Array.from({ length: n }, (_, i) => `${(width * i) ** 2}`).join(' + ') },
          { label: 'Add them.', latex: `${heights}` },
          { label: 'Multiply by the width, once.', latex: `${heights} \\times ${width} = ${answer}` },
        ],
        traps: [
          // Heights summed and never multiplied by the width.
          trap(heights, 'riemann-width-not-applied'),
          // The right-endpoint sum: same method, wrong side of every strip.
          trap(rightHeights * width, 'riemann-endpoint-side-swapped'),
        ],
      });
    },
  }),

  template({
    id: 'riemann-over-or-under',
    conceptId: 'integrals',
    description: 'Say whether a rectangle sum is too big or too small.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const n = rng.int(4, 8);

      return choiceExercise({
        prompt: `On a curve that rises across the whole interval, ${n} left-endpoint rectangles are used to estimate the area. The estimate is:`,
        ...options(
          [
            { label: 'Too small — every rectangle sits below the curve', correct: true },
            { label: 'Too big — every rectangle pokes above the curve', misconceptionId: 'riemann-endpoint-side-swapped' },
            { label: 'Exact' },
            { label: 'Impossible to say without the function' },
          ],
          rng.shuffle,
        ),
        summary:
          'On a rising curve the left edge of each strip is where the curve is lowest across that strip, so every rectangle is short and the total falls below the true area. Right endpoints do the opposite, and the exact value is trapped between the two.',
        hint: 'On a rising curve, where in each strip is the function smallest?',
      });
    },
  }),

  template({
    id: 'definite-integral-signed',
    conceptId: 'integrals',
    description: 'Distinguish a signed integral from an area.',
    difficulties: [3, 4],
    build: ({ rng }) =>
      choiceExercise({
        prompt: 'A curve lies partly above and partly below the x-axis between a and b, and the definite integral over [a, b] comes out as 0. What does that mean?',
        ...options(
          [
            { label: 'There is as much area below the axis as above it', correct: true },
            { label: 'The region has no area', misconceptionId: 'riemann-width-not-applied' },
            { label: 'The curve is zero everywhere on [a, b]' },
            { label: 'A mistake has been made — an integral cannot be zero' },
          ],
          rng.shuffle,
        ),
        summary:
          'An integral is a signed area: strips below the axis contribute negative amounts. Zero means the two sides cancelled, not that there was nothing there. For the actual area, split the interval at each root and add the sizes.',
        hint: 'What sign does a rectangle below the axis contribute?',
      }),
  }),

  /* -------------------------------------------------- fundamental theorem */
  template({
    id: 'definite-integral-polynomial',
    conceptId: 'fundamental-theorem',
    description: 'Evaluate a definite integral with an antiderivative.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = 3 * rng.int(1, 3);
      const b = 2 * rng.int(1, 4);
      const lower = rng.int(0, 3);
      const upper = lower + rng.int(1, 3);
      const F = (x: number): number => (a / 3) * x ** 3 + (b / 2) * x * x;
      const answer = F(upper) - F(lower);

      return numericExercise({
        prompt: 'Evaluate the definite integral.',
        latex: `\\int_{${lower}}^{${upper}} \\big(${expr(`${a}x^{2}`, signedTerm(b, 'x'))}\\big)\\,dx`,
        value: answer,
        summary: `An antiderivative is F(x) = ${a / 3}x³ + ${b / 2}x². Then F(${upper}) = ${F(upper)} and F(${lower}) = ${F(lower)}, so the integral is ${F(upper)} − ${F(lower)} = ${answer}.`,
        hint: 'Antidifferentiate, then subtract: top limit minus bottom limit.',
        steps: [
          { label: 'Antidifferentiate — any one will do, so take C = 0.', latex: `F(x) = ${a / 3}x^{3} + ${b / 2}x^{2}` },
          { label: 'Substitute the top limit.', latex: `F(${upper}) = ${F(upper)}` },
          { label: 'Substitute the bottom limit.', latex: `F(${lower}) = ${F(lower)}` },
          { label: 'Subtract, top first.', latex: `${F(upper)} - ${F(lower)} = ${answer}` },
        ],
        /*
         * The reversed subtraction is only a distinct answer when the integral
         * is non-zero — and with a positive integrand on an interval of
         * positive width it always is.
         */
        traps: [...(answer === 0 ? [] : [trap(-answer, 'ftc-limits-subtracted-backwards')])],
      });
    },
  }),

  template({
    id: 'ftc-no-constant',
    conceptId: 'fundamental-theorem',
    description: 'Recognise that a definite integral carries no + C.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const k = rng.int(2, 6);
      const value = k * 4;

      return choiceExercise({
        prompt: 'Which of these is a possible answer to a definite integral?',
        latex: `\\int_{0}^{2} ${k * 2}x\\,dx`,
        ...options(
          [
            { latex: `${value}`, correct: true },
            { latex: `${value} + C`, misconceptionId: 'definite-integral-given-constant' },
            { latex: `${k}x^{2} + C`, misconceptionId: 'antiderivative-constant-omitted' },
            { latex: `-${value}`, misconceptionId: 'ftc-limits-subtracted-backwards' },
          ],
          rng.shuffle,
        ),
        summary: `An antiderivative is ${k}x², so the integral is ${k}(4) − ${k}(0) = ${value}. A definite integral is a number: whichever antiderivative you pick, the constant appears in both F(b) and F(a) and cancels in the subtraction.`,
        hint: 'What happens to a + C when you compute F(b) − F(a)?',
      });
    },
  }),

  template({
    id: 'net-change-from-rate',
    conceptId: 'fundamental-theorem',
    description: 'Integrate a rate to get the total change.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rate = 2 * rng.int(1, 5);
      const upper = rng.int(2, 6);
      const answer = (rate / 2) * upper * upper;

      return numericExercise({
        prompt: `A tank fills at a rate of ${rate}t litres per minute. How much water enters between t = 0 and t = ${upper} minutes?`,
        unit: 'litres',
        value: answer,
        summary: `Integrating the rate gives the total change: ∫₀^${upper} ${rate}t dt = [${rate / 2}t²]₀^${upper} = ${answer} litres.`,
        hint: 'Integrating a rate of change over an interval gives the net change over it.',
        steps: [
          { label: 'Antidifferentiate the rate.', latex: `\\int ${rate}t\\,dt = ${rate / 2}t^{2}` },
          { label: 'Evaluate between the limits.', latex: `${rate / 2}(${upper})^{2} - ${rate / 2}(0)^{2}` },
          { label: 'Arithmetic.', latex: `${answer}` },
        ],
        // Reading the rate at the final instant as though it were the total.
        traps: [...(rate * upper === answer ? [] : [trap(rate * upper, 'riemann-width-not-applied')])],
      });
    },
  }),
];

/** Rounds to two decimal places, the precision the ladder answers are asked to. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
